/**
 * 会社別(⑥指示: 日次／週次／月次を切替)。親=会社／子=出勤形態／孫=アポインター の3階層(行クリックで開閉。既定は閉じる)。
 * 列: 会社名/売上単価/取得件数/内訳(出勤形態)/売上見込み/納品件数/売上実績/報酬(原価)/原価率/粗利/粗利率
 *  - 売上見込み = 取得件数 × 売上単価 / 売上実績 = 納品件数 × 売上単価(納品件数は「売上実績」ページの手入力)
 *  - 納品件数は会社×(品川／歩合／時給)で入力されるため、子=出勤形態の行にも納品件数・売上実績を出す
 *  - 原価率・粗利は売上実績が入っていれば実績、無ければ見込みで計算(列見出しに明記)
 */
import { useMemo, useState } from 'react';
import HelpTip from '@core/components/HelpTip';
import { IconChevronRight } from '@core/components/icons';
import MultiSelect, { matchesFilter } from '@core/components/MultiSelect';
import { ChipSelect, SearchBox, SortTh, matchKeyword, sortRows, useSort } from '@core/components/tableTools';
import { formatYen } from '@core/lib/money';
import { BUSINESS_LABELS, DELIVERY_STYLE_LABELS, DELIVERY_STYLES, type BusinessType, type Company, type DeliveryStyle } from '@core/lib/types';
import { CATEGORY_KEYS, CATEGORY_ROW_LABELS, categoryRows, costRate, fmtPct, grossOf, groupByCompany, type Aggregate, type CompanyTree } from './calc';
import { HELP, costRateTone, costRateVerdict, grossRateVerdict } from './colors';
import PeriodToolbar from './PeriodToolbar';
import { usePeriodData } from './usePeriod';

type SortKey = 'name' | 'unit_price' | 'appointments' | 'revenue' | 'delivered' | 'actual' | 'pay' | 'rate' | 'gross' | 'gross_rate';

/** 1行分の計算(会社・出勤形態・アポインターで共通) */
function lineOf(agg: Aggregate, unitPrice: number | null, delivered: number | null) {
  const actual = delivered !== null && unitPrice !== null ? delivered * unitPrice : null;
  const base = actual ?? agg.revenue;
  const rate = base > 0 ? (agg.pay / base) * 100 : null;
  const gross = base - agg.pay;
  return { actual, rate, gross, grossRate: grossOf({ ...agg, revenue: base }).rate };
}

function Row({
  level,
  label,
  extra,
  unitPrice,
  agg,
  breakdown,
  delivered,
  open,
  onToggle,
  costTarget,
}: {
  level: 0 | 1 | 2;
  label: string;
  extra?: string;
  unitPrice: number | null;
  agg: Aggregate;
  breakdown: string;
  /** 納品件数。null = 未入力 */
  delivered: number | null;
  open?: boolean;
  onToggle?: () => void;
  costTarget: number;
}) {
  const l = lineOf(agg, unitPrice, delivered);
  const rv = costRateVerdict(l.rate, costTarget);
  const gv = grossRateVerdict(l.grossRate);
  return (
    <tr className={`tree-l${level}${onToggle ? ' clickable' : ''}`} onClick={onToggle} aria-expanded={onToggle ? open : undefined}>
      <td style={{ paddingLeft: 8 + level * 22 }}>
        {onToggle ? (
          <span className={`chev${open ? ' open' : ''}`}>
            <IconChevronRight size={12} />
          </span>
        ) : level > 0 ? (
          <span className="hint" style={{ display: 'inline-block', width: 18 }}>
            └
          </span>
        ) : (
          <span style={{ display: 'inline-block', width: 14 }} />
        )}
        {label}
        {extra && <span className="hint" style={{ fontWeight: 400 }}> {extra}</span>}
      </td>
      <td className="num">{unitPrice === null ? '' : `¥${formatYen(unitPrice)}`}</td>
      <td className="num">{agg.appointments}</td>
      <td className="hint wrap">{breakdown}</td>
      <td className="num">¥{formatYen(agg.revenue)}</td>
      <td className="num">{delivered === null ? '' : delivered}</td>
      <td className="num">{l.actual === null ? '' : `¥${formatYen(l.actual)}`}</td>
      <td className="num">¥{formatYen(agg.pay)}</td>
      <td className={`num${costRateTone(l.rate, costTarget) === 'over' ? ' neg' : ''}`}>
        {rv ? (
          <HelpTip help={HELP.costRate} verdict={rv} value={fmtPct(l.rate)}>
            {fmtPct(l.rate)}
          </HelpTip>
        ) : (
          fmtPct(l.rate)
        )}
      </td>
      <td className={`num${l.gross < 0 ? ' neg' : ''}`}>¥{formatYen(l.gross)}</td>
      <td className="num">
        {gv ? (
          <HelpTip help={HELP.grossRate} verdict={gv} value={fmtPct(l.grossRate)}>
            {fmtPct(l.grossRate)}
          </HelpTip>
        ) : (
          fmtPct(l.grossRate)
        )}
      </td>
    </tr>
  );
}

/** 出勤形態の行ラベル(品川/時給 など)に対応する納品内訳キー */
function deliveryStyleOfLabel(label: string): DeliveryStyle | null {
  for (const k of DELIVERY_STYLES) if (DELIVERY_STYLE_LABELS[k] === label) return k;
  return null;
}

export default function CompaniesSummaryPage() {
  const period = usePeriodData();
  const { rows, companies, deliveries, targets, loading, range } = period;
  const [companyOpen, setCompanyOpen] = useState<Set<string>>(new Set());
  const [styleOpen, setStyleOpen] = useState<Set<string>>(new Set());
  const [activeOnly, setActiveOnly] = useState<'active' | 'all'>('active');
  const [bizF, setBizF] = useState<Set<BusinessType>>(new Set());
  const [keyword, setKeyword] = useState('');
  const { sort, toggle: toggleSort } = useSort<SortKey>(null);
  // 原価率の赤は 給湯器=cost_rate_total(既定45%) / 不用品=cost_rate_fuyohin(既定50%)。月次設定で変えられる(2026-09-24)
  const costTarget = targets?.cost_rate_total ?? 45;
  const fuyohinTarget = targets?.cost_rate_fuyohin ?? 50;

  const active = useMemo(() => companies.filter((c: Company) => c.status === 'active'), [companies]);
  const trees: CompanyTree[] = useMemo(() => groupByCompany(rows, active, range.to, deliveries), [rows, active, range.to, deliveries]);
  const companyMap = useMemo(() => new Map(companies.map((c) => [c.id, c])), [companies]);
  /** その会社の目標原価率(不用品の会社は不用品の目標) */
  const targetOfCompany = (id: string | null) => (companyMap.get(id ?? '')?.product_type === 'fuyohin' ? fuyohinTarget : costTarget);

  /** 期間内の納品件数(会社別合計と 品川／歩合／時給 の内訳)。1日も入力が無ければ null */
  const delivered = useMemo(() => {
    if (deliveries.length === 0) return null;
    const total: Record<string, number> = {};
    const byStyle: Record<string, Partial<Record<DeliveryStyle, number>>> = {};
    for (const d of deliveries) {
      for (const [k, v] of Object.entries(d.counts ?? {})) total[k] = (total[k] ?? 0) + v;
      for (const [k, det] of Object.entries(d.details ?? {})) {
        const cur = byStyle[k] ?? {};
        for (const s of DELIVERY_STYLES) if (det.by_style[s] !== undefined) cur[s] = (cur[s] ?? 0) + (det.by_style[s] ?? 0);
        byStyle[k] = cur;
      }
    }
    return { total, byStyle };
  }, [deliveries]);

  const visible = useMemo(() => {
    const list = trees.filter((t) => {
      const c = t.company_id ? companyMap.get(t.company_id) : undefined;
      const del = delivered && t.company_id ? (delivered.total[t.company_id] ?? 0) : 0;
      if (activeOnly === 'active' && t.agg.appointments === 0 && del === 0) return false;
      if (!(c ? matchesFilter(bizF, c.product_type) : bizF.size === 0)) return false;
      return matchKeyword(keyword, t.name, t.company_id);
    });
    return sortRows(list, sort, (t, key) => {
      const del = delivered && t.company_id ? (delivered.total[t.company_id] ?? null) : null;
      const l = lineOf(t.agg, t.unit_price, del);
      switch (key) {
        case 'name':
          return t.name;
        case 'unit_price':
          return t.unit_price;
        case 'appointments':
          return t.agg.appointments;
        case 'revenue':
          return t.agg.revenue;
        case 'delivered':
          return del;
        case 'actual':
          return l.actual;
        case 'pay':
          return t.agg.pay;
        case 'rate':
          return l.rate;
        case 'gross':
          return l.gross;
        case 'gross_rate':
          return l.grossRate;
      }
    });
  }, [trees, activeOnly, bizF, keyword, sort, delivered, companyMap]);

  const cats = useMemo(() => categoryRows(rows), [rows]);
  const total = cats.total;
  const totalDelivered = delivered ? Object.values(delivered.total).reduce((a, b) => a + b, 0) : null;
  const totalActual = delivered
    ? trees.reduce((s, t) => s + (t.company_id && delivered.total[t.company_id] !== undefined && t.unit_price !== null ? delivered.total[t.company_id]! * t.unit_price : 0), 0)
    : null;
  const totalLine = lineOf(total, null, null);
  const totalBase = totalActual ?? total.revenue;
  const totalRate = totalBase > 0 ? (total.pay / totalBase) * 100 : null;

  const toggleSet = (setter: (f: (prev: Set<string>) => Set<string>) => void, key: string) =>
    setter((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const breakdownOf = (agg: Aggregate, list: { label: string; agg: Aggregate }[]) =>
    agg.appointments === 0 ? '—' : list.map((s) => `${s.label} ${s.agg.appointments}`).join(' / ');

  return (
    <div className="screen master-screen full">
      <PeriodToolbar period={period} />
      {loading ? (
        <div className="card">
          <div className="empty">読み込み中…</div>
        </div>
      ) : (
        <div className="card">
          <h3 style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            会社別({range.label})
            <span className="hint" style={{ fontWeight: 400 }}>行クリックで出勤形態・アポインター別を開閉</span>
            <span style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
              <button
                className="txn-link"
                onClick={() => {
                  setCompanyOpen(new Set(trees.map((t) => t.company_id ?? '')));
                  setStyleOpen(new Set(trees.flatMap((t) => t.styles.map((s) => `${t.company_id ?? ''}|${s.key}`))));
                }}
              >
                すべて開く
              </button>
              <button
                className="txn-link"
                onClick={() => {
                  setCompanyOpen(new Set());
                  setStyleOpen(new Set());
                }}
              >
                すべて閉じる
              </button>
            </span>
          </h3>
          <div className="toolbar" style={{ marginBottom: 10 }}>
            <div className="tool-filter" style={{ width: '100%', rowGap: 6 }}>
              <ChipSelect
                options={[
                  { key: 'active', label: '稼働している会社のみ' },
                  { key: 'all', label: '会社マスタの全社' },
                ]}
                value={activeOnly}
                onChange={(v) => setActiveOnly(v === 'all' ? 'all' : 'active')}
                allLabel={null}
              />
              <MultiSelect label="商材" options={(Object.keys(BUSINESS_LABELS) as BusinessType[]).map((k) => ({ key: k, label: BUSINESS_LABELS[k] }))} value={bizF} onChange={setBizF} />
              <SearchBox value={keyword} onChange={setKeyword} placeholder="社名で検索" />
            </div>
          </div>
          <div className="table-scroll">
          <table className="fit-table tree-table">
            <thead>
              <tr>
                <SortTh label="会社名" sortKey="name" sort={sort} onSort={toggleSort} defaultDir="asc" />
                <SortTh label="売上単価" sortKey="unit_price" sort={sort} onSort={toggleSort} className="num" />
                <SortTh label="取得件数" sortKey="appointments" sort={sort} onSort={toggleSort} className="num" />
                <th>内訳(出勤形態)</th>
                <SortTh label="売上見込み" sortKey="revenue" sort={sort} onSort={toggleSort} className="num" help={HELP.revenue} />
                <SortTh
                  label="納品件数"
                  sortKey="delivered"
                  sort={sort}
                  onSort={toggleSort}
                  className="num"
                  help={{ title: '納品件数', what: '翌日に各会社へ納品した件数(キャンセル控除後)。「日報/サマリ」ページで会社×出勤形態ごとに入力', better: '多いほど良い' }}
                />
                <SortTh label="売上実績" sortKey="actual" sort={sort} onSort={toggleSort} className="num" help={HELP.actualSales} />
                <SortTh label="報酬(原価)" sortKey="pay" sort={sort} onSort={toggleSort} className="num" />
                <SortTh label="原価率" sortKey="rate" sort={sort} onSort={toggleSort} className="num" defaultDir="asc" help={HELP.costRate} />
                <SortTh label="粗利" sortKey="gross" sort={sort} onSort={toggleSort} className="num" />
                <SortTh label="粗利率" sortKey="gross_rate" sort={sort} onSort={toggleSort} className="num" help={HELP.grossRate} />
              </tr>
            </thead>
            <tbody>
              {visible.length === 0 && (
                <tr>
                  <td colSpan={11} className="hint">
                    該当する会社がありません
                  </td>
                </tr>
              )}
              {visible.map((t) => {
                const cid = t.company_id ?? '';
                const open = companyOpen.has(cid);
                const del = delivered && t.company_id ? (delivered.total[t.company_id] ?? 0) : null;
                return [
                  <Row
                    key={`c_${cid}`}
                    level={0}
                    label={t.name}
                    extra={t.agg.appointments === 0 ? '(取得なし)' : undefined}
                    unitPrice={t.unit_price}
                    agg={t.agg}
                    breakdown={breakdownOf(t.agg, t.styles)}
                    delivered={del}
                    open={open}
                    onToggle={t.styles.length > 0 ? () => toggleSet(setCompanyOpen, cid) : undefined}
                    costTarget={targetOfCompany(t.company_id)}
                  />,
                  ...(open
                    ? t.styles.flatMap((s) => {
                        const skey = `${cid}|${s.key}`;
                        const sopen = styleOpen.has(skey);
                        const dk = deliveryStyleOfLabel(s.label);
                        const sdel = delivered && t.company_id && dk ? (delivered.byStyle[t.company_id]?.[dk] ?? null) : null;
                        return [
                          <Row
                            key={`s_${skey}`}
                            level={1}
                            label={s.label}
                            extra={`${s.appointers.length}名`}
                            unitPrice={t.unit_price}
                            agg={s.agg}
                            breakdown={`${s.label} ${s.agg.appointments}`}
                            delivered={sdel}
                            open={sopen}
                            onToggle={() => toggleSet(setStyleOpen, skey)}
                            costTarget={targetOfCompany(t.company_id)}
                          />,
                          ...(sopen
                            ? s.appointers.map((a) => (
                                <Row key={`a_${skey}|${a.op_id}`} level={2} label={a.name} unitPrice={t.unit_price} agg={a.agg} breakdown="" delivered={null} costTarget={targetOfCompany(t.company_id)} />
                              ))
                            : []),
                        ];
                      })
                    : []),
                ];
              })}
            </tbody>
            <tfoot>
              <tr style={{ fontWeight: 700, background: 'var(--legacy-shade)' }}>
                <td>合計(全社)</td>
                <td></td>
                <td className="num">{total.appointments}</td>
                <td className="hint wrap">
                  {breakdownOf(
                    total,
                    CATEGORY_KEYS.filter((k) => ['shinagawa', 'zaitaku_jikyu', 'buai', 'white'].includes(k))
                      .map((k) => ({ label: CATEGORY_ROW_LABELS[k], agg: cats[k] }))
                      .filter((x) => x.agg.appointments > 0),
                  )}
                </td>
                <td className="num">¥{formatYen(total.revenue)}</td>
                <td className="num">{totalDelivered ?? ''}</td>
                <td className="num">{totalActual === null ? '' : `¥${formatYen(totalActual)}`}</td>
                <td className="num">¥{formatYen(total.pay)}</td>
                <td className={`num${costRateTone(totalRate, costTarget) === 'over' ? ' neg' : ''}`}>
                  {(() => {
                    const v = costRateVerdict(totalRate, costTarget);
                    return v ? (
                      <HelpTip help={HELP.costRate} verdict={v} value={fmtPct(totalRate)}>
                        {fmtPct(totalRate)}
                      </HelpTip>
                    ) : (
                      fmtPct(totalRate ?? costRate(total))
                    );
                  })()}
                </td>
                <td className="num">¥{formatYen(totalBase - total.pay)}</td>
                <td className="num">{fmtPct(grossOf({ ...total, revenue: totalBase }).rate ?? totalLine.grossRate)}</td>
              </tr>
            </tfoot>
          </table>
          </div>
        </div>
      )}
    </div>
  );
}
